import Cookies from "js-cookie";
import deviceInfo from "mobile-device-detect";

let ready = false;

const settings = window.beamr || {};
const supportedFiles = ["image/jpeg"];
const optimisationErrors = [
  "JMU_ERR_OPTIMIZE_ERROR",
  "JMU_ERR_HIGHLY_COMPRESSED",
  "JMU_ERR_ALREADY_JM_OPTIMIZED",
  "JMU_ERR_FILE_FORMAT_INVALID",
  "JMU_ERR_MEMORY_ALLOCATION"
];

if (settings.key) {
  const beamrLibrary = document.createElement('script');

  beamrLibrary.src =
    '/javascripts/libs/beamr/jpegmini_uploader512MB_1.3.5.201702743.js';
  document.body.appendChild(beamrLibrary);

  beamrLibrary.onload = () => {
    ready = true;
  };
}

const disableBeamr = () => Cookies.set('beamrFailure', 'true', { expires: 1 });

const uploadViaBeamr = () => {
  if (!settings.key) {
    return false;
  }

  if (Cookies.get('beamrFailure') === 'true') {
    return false;
  }

  function configuratorMatrixPredicate(matrix) {
    // matrix is of the form
    // {"device":"isBrowser","browser":"isSafari"}
    // or
    // {"device":"isMobileOnly","operatingSystem":"isAndroid"}
    // All keys are optional. It might contain none!, all, or some mix of values.
    // Filter down the present keys first and then test for their validity in the current browser

    return ['browser', 'device', 'operatingSystem']
      .filter(key => matrix[key])
      .every(key => {
        const attribute = matrix[key];
        return deviceInfo[attribute];
      });
  }

  return (settings.configuratorMatrix || []).some(configuratorMatrixPredicate);
};

const suitableBeamrFile = file =>
  file.service === 'upload' &&
  supportedFiles.includes(file.type) &&
  !file.beamrFailed;

const removeHTTP = url => {
  if (url[0] === '/') {
    return url;
  }

  let result = '';
  let count = 0;
  const threshold = url.indexOf('http') < 0 ? 0 : 2;

  [...url].forEach(character => {
    character === '/' && count++;

    if (count > threshold) {
      result += character;
    }
  });

  return result;
};

function setURL(uploadURL) {
  const path = removeHTTP(uploadURL);
  return `${window.location.protocol}//${window.location.host}${path}`;
}

function logEvent(event, message) {
  console.debug(
    `name:${event.file && event.file.name}`,
    `id:${event.file &&
      event.file.jmu_user_data &&
      event.file.jmu_user_data.beamrId}`,
    message,
    `type:${event.type_str}`,
    `error:${event.error_str}`,
    '\n',
    `time:${event.elapsed_time_ms || '?'}ms`,
    'times:',
    event.file &&
      event.file.jmu_user_data &&
      event.file.jmu_user_data.uploadTimings,
    '\n',
    'summary:',
    event.summary
  );
}

function handleBeamrEvent(event, uploadResponse, callBack) {
  logEvent(event, 'Beamr event received');
  if (!event) {
    return;
  }

  if (typeof callBack !== 'function') {
    callBack = () => {};
  }

  const compressionStageSucceeded =
    event.type_str === 'JMU_RESULT_FILE_OPTIMIZE_OK';
  const compressionStageFailed =
    event.error_code !== 0 && !optimisationErrors.includes(event.type_str);
  const attemptSuccessful = event.type_str === 'JMU_RESULT_FILE_UPLOAD_OK';
  const attemptFailed = event.error_code === 'JMU_ERR_UPLOAD_ERROR';
  const attemptCompleted =
    event.type_str === 'JMU_RESULT_FILE_PROCESS_COMPLETE';

  const attemptStarted = event.type_str === 'JMU_RESULT_FILE_SUBMIT_OK';
  if (attemptStarted) {
    logEvent(event, 'Image compression started');
  }

  if (compressionStageFailed) {
    event.file.jmu_user_data.uploadTimings.compressed();
    return callBack(false, uploadResponse, event);
  }

  if (compressionStageSucceeded) {
    event.file.jmu_user_data.uploadTimings.compressed();
    logEvent(event, 'Image compressed successfully!');
    return;
  }

  if (attemptSuccessful) {
    logEvent(event, 'Beamr upload successful');
    event.file.jmu_user_data.uploadTimings.uploaded();
    event.file.jmu_user_data.uploadResponse = uploadResponse;
  }

  if (attemptFailed) {
    logEvent(event, 'Beamr upload failed (file process complete)');
    return callBack(false, uploadResponse, event);
  }

  if (attemptCompleted) {
    logEvent(event, 'Beamr attempt successful');
    event.file.jmu_user_data.uploadAnalytics.setFileSizes(
      event.summary.optimization.original_size,
      event.summary.optimization.optimized_size
    );
    return callBack(true, event.file.jmu_user_data.uploadResponse, event);
  }
}

function shouldDisableBeamr(files, beamrAttempts) {
  return (
    files.length > 2 &&
    (Object.keys(beamrAttempts).length / files.length) * 100 > 65
  );
}

function config(uploadURL, file, extraStuff, callBack) {
  return {
    debug: !!window.debugBeamr,
    license_key: settings.key,
    upload_url: setURL(uploadURL),
    custom_headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': (() => {
        const csrfToken = document.head.querySelector("[name='csrf-token']");
        return csrfToken && csrfToken.content;
      })(),
    },
    upload_type: 0,
    custom_form_fields: { source: file.service, album: extraStuff.album },
    error_policy: {
      upload_originals_on_optimisation_error: false,
      pause_on_upload_error: false,
      upload_originals_on_allocation_error: false,
      pause_on_allocation_error: false,
    },
    result_callback: (_error, result) => {
      let response = '';
      if (result.type_str === 'JMU_RESULT_FILE_UPLOAD_OK') {
        response = JSON.parse(result.http_response.body);
      }

      // callBack is GenericPaginatedUploadMixin#onBeamrDone(didWork[bool], data[Object], event[Object])
      handleBeamrEvent(result, response, callBack);
    },
  };
}

function configure(conf) {
  if (ready) {
    return JMUploader.configure(conf);
  }
}

// callBack is GenericPaginatedUploadMixin#successOrFailure(didWork[bool], data[Object])
function uploadCompressed(uploadURL, file, additionalData, callBack) {
  if (configure(config(uploadURL, file, additionalData, callBack)) === 0) {
    JMUploader.start();
  }

  file.jmu_user_data = {
    attempts: file.attempts,
    beamrId: file.beamrId,
    uploadTimings: file.uploadTimings,
    uploadAnalytics: file.uploadAnalytics,
  };
  console.debug('Submitting file to Beamr:', file.name, file.beamrId, file);
  JMUploader.submit_files([file]);
}

export {
  uploadViaBeamr,
  suitableBeamrFile,
  shouldDisableBeamr,
  disableBeamr,
  uploadCompressed,
};
